import { useState } from "react";
import { useLocation } from "wouter";
import {
  Download,
  Trash2,
  Users,
  User,
  Sun,
  Moon,
  Monitor,
  Sparkles,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Eye,
  Keyboard,
  Check,
  SlidersHorizontal,
  Bell,
  ShieldCheck,
  Plug,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/EmptyState";
import { useUiStore } from "@/lib/stores/uiStore";
import { useAuthStore } from "@/lib/stores/authStore";
import { integrations, planUsage } from "@/lib/data/extras";

const TABS = [
  { key: "cuenta", label: "Cuenta", icon: User },
  { key: "preferencias", label: "Preferencias", icon: SlidersHorizontal },
  { key: "notificaciones", label: "Notificaciones", icon: Bell },
  { key: "privacidad", label: "Privacidad y datos", icon: ShieldCheck },
  { key: "integraciones", label: "Integraciones", icon: Plug },
  { key: "atajos", label: "Atajos de teclado", icon: Keyboard },
  { key: "equipo", label: "Equipo", icon: Users },
  { key: "acerca", label: "Acerca de", icon: Info },
];

const SettingRow = ({ label, value, last }) => (
  <button
    type="button"
    onClick={() => toast("Configurable en la versión final.")}
    className={cn("w-full flex items-center justify-between py-2.5 text-sm hover:bg-secondary/40 -mx-1 px-1 rounded transition-colors", !last && "border-b")}
  >
    <span className="text-muted-foreground">{label}</span>
    <span className="flex items-center gap-1 font-medium">
      {value} <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" strokeWidth={1.75} />
    </span>
  </button>
);

const ThemeOption = ({ icon: Icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`theme-option-${label.toLowerCase()}`}
    className={cn(
      "flex-1 rounded-lg border p-3 grid place-items-center gap-1.5 text-sm transition-colors",
      active ? "border-primary/60 bg-accent/50 text-foreground" : "text-muted-foreground hover:border-teal-300/60",
    )}
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    {label}
  </button>
);

export default function Ajustes() {
  const { theme, setTheme } = useUiStore();
  const { user } = useAuthStore();
  const [, navigate] = useLocation();
  const [tab, setTab] = useState("cuenta");
  const [avisoChat, setAvisoChat] = useState(true);
  const [marcaIA, setMarcaIA] = useState(true);

  const aiPct = Math.round((planUsage.aiUsed / planUsage.aiQuota) * 100);

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="settings-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Ajustes</h1>
            <p className="mt-1.5 text-muted-foreground text-[15px]">Gestiona tu cuenta, preferencias y datos.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => toast("Perfil público en la versión final.")}>
            <User className="h-3.5 w-3.5" strokeWidth={1.75} /> Ir a mi perfil
          </Button>
        </div>

        {/* Tabs horizontales */}
        <div className="mt-5 border-b flex gap-1 overflow-x-auto scrollbar-thin">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`settings-tab-${t.key}`}
              className={cn(
                "relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors",
                tab === t.key ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <t.icon className="h-4 w-4" strokeWidth={1.75} />
              {t.label}
              {tab === t.key && <span className="absolute inset-x-2 -bottom-px h-0.5 bg-primary rounded-full" />}
            </button>
          ))}
        </div>

        {tab !== "cuenta" ? (
          <div className="mt-6 rounded-lg border bg-card max-w-2xl">
            <EmptyState
              icon={TABS.find((t) => t.key === tab)?.icon}
              title={`${TABS.find((t) => t.key === tab)?.label} — sección de demostración`}
              hint="En este prototipo solo la pestaña «Cuenta» está desarrollada."
            />
          </div>
        ) : (
          <div className="mt-6 grid gap-4 lg:grid-cols-3 items-start">
            {/* Columna 1 */}
            <div className="space-y-4">
              <Card data-testid="settings-account-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Perfil</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground grid place-items-center text-base font-semibold">
                      {user?.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => toast("Edición de perfil en la versión final.")}>
                      Editar perfil
                    </Button>
                  </div>
                  <div className="mt-4">
                    <SettingRow label="Zona horaria" value="Europa/Madrid (GMT+2)" />
                    <SettingRow label="Idioma" value="Español (España)" />
                    <SettingRow label="Moneda" value="EUR (€)" last />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="settings-appearance-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Apariencia</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <ThemeOption icon={Sun} label="Claro" active={theme === "light"} onClick={() => setTheme("light")} />
                    <ThemeOption icon={Moon} label="Oscuro" active={theme === "dark"} onClick={() => setTheme("dark")} />
                    <ThemeOption
                      icon={Monitor}
                      label="Sistema"
                      onClick={() => {
                        setTheme(window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
                        toast("Tema ajustado al sistema.");
                      }}
                    />
                  </div>
                  <div className="mt-4">
                    <SettingRow label="Tamaño de fuente" value="Normal" />
                    <SettingRow label="Densidad de interfaz" value="Cómoda" />
                    <SettingRow label="Vista por defecto" value="Biblioteca (lista)" last />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna 2 */}
            <div className="space-y-4">
              <Card data-testid="settings-plan-card">
                <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Plan</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => toast("Gestión del plan en la versión final.")}>
                    Gestionar plan
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="flex items-center gap-2 text-sm">
                    <span className="rounded-md bg-primary text-primary-foreground px-2 py-0.5 text-xs font-semibold">Pro</span>
                    Plan anual
                  </p>
                  <div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Resúmenes IA / mes</span>
                      <span className="font-medium tabular-nums">{planUsage.aiQuota.toLocaleString("es-ES")}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${aiPct}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground tabular-nums">{planUsage.aiUsed.toLocaleString("es-ES")} usados este mes</p>
                  </div>
                  <div className="text-sm space-y-2">
                    <p className="flex items-center justify-between">
                      <span className="text-muted-foreground">Almacenamiento</span>
                      <span className="font-medium tabular-nums">
                        {planUsage.storageUsed} de {planUsage.storageTotal}
                      </span>
                    </p>
                    <p className="flex items-center justify-between">
                      <span className="text-muted-foreground">Historial de versiones</span>
                      <span className="font-medium">Disponible</span>
                    </p>
                    <p className="flex items-center justify-between">
                      <span className="text-muted-foreground">Soporte prioritario</span>
                      <span className="font-medium">Disponible</span>
                    </p>
                  </div>
                  <button type="button" onClick={() => toast("Facturación en la versión final.")} className="flex items-center gap-1 text-[13px] text-primary font-medium hover:underline">
                    Ver mi facturación <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </CardContent>
              </Card>

              <Card data-testid="settings-ai-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Transparencia IA</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg bg-accent/60 border border-primary/20 px-3 py-2.5 flex items-start gap-2 text-[13px]">
                    <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.75} />
                    <span className="flex-1">
                      Estás interactuando con un asistente de IA. Puede cometer errores — verifica la información importante.
                    </span>
                    <button type="button" onClick={() => toast("AI Act, art. 50: transparencia obligatoria.")} className="text-primary font-medium hover:underline shrink-0">
                      Saber más
                    </button>
                  </div>
                  <label className="flex items-center justify-between text-sm cursor-pointer">
                    Mostrar aviso en cada chat
                    <Switch checked={avisoChat} onCheckedChange={setAvisoChat} aria-label="Mostrar aviso en cada chat" />
                  </label>
                  <label className="flex items-center justify-between text-sm cursor-pointer">
                    Incluir marca "Resumen generado por IA"
                    <Switch checked={marcaIA} onCheckedChange={(v) => { setMarcaIA(v); if (!v) { toast("La marca es obligatoria (AI Act, art. 50)."); setTimeout(() => setMarcaIA(true), 600); } }} aria-label="Incluir marca de resumen generado por IA" />
                  </label>
                  <button type="button" onClick={() => toast("Página de transparencia en la versión final.")} className="flex items-center gap-1 text-[13px] text-primary font-medium hover:underline">
                    Ver cómo usamos la IA <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </CardContent>
              </Card>
            </div>

            {/* Columna 3 */}
            <div className="space-y-4">
              <Card data-testid="settings-integrations-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Integraciones destacadas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {integrations.slice(0, 4).map((i) => (
                    <div key={i.id} className="flex items-center gap-2.5 text-sm">
                      <span className="w-8 h-8 rounded-lg border bg-secondary grid place-items-center shrink-0">
                        <Plug className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                      </span>
                      <span className="flex-1 font-medium">{i.name}</span>
                      {i.connected ? (
                        <span className="flex items-center gap-1 text-[13px] text-green-700 dark:text-green-400 font-medium">
                          <Check className="h-3.5 w-3.5" strokeWidth={2} /> Conectado
                        </span>
                      ) : (
                        <button type="button" onClick={() => toast(`Conexión con ${i.name} en la versión final.`)} className="text-[13px] text-primary font-medium hover:underline">
                          Conectar
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => navigate("/integraciones")} className="flex items-center gap-1 text-[13px] text-primary font-medium hover:underline pt-1">
                    Ver todas las integraciones <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </CardContent>
              </Card>

              <Card data-testid="settings-shortcuts-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Atajos de teclado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {[
                    ["Capturar contenido", "⌘ K"],
                    ["Búsqueda rápida", "⌘ P"],
                    ["Abrir asistente", "⌘ J"],
                    ["Crear tarea", "⌘ T"],
                  ].map(([l, k]) => (
                    <p key={l} className="flex items-center justify-between text-muted-foreground">
                      {l} <kbd className="rounded border bg-secondary px-1.5 py-0.5 text-[11px] text-foreground">{k}</kbd>
                    </p>
                  ))}
                  <button type="button" onClick={() => toast("Lista completa en la versión final.")} className="flex items-center gap-1 text-[13px] text-primary font-medium hover:underline pt-1">
                    Ver todos los atajos <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </CardContent>
              </Card>

              <Card className="border-dashed bg-secondary/40" data-testid="settings-teams-card">
                <CardContent className="pt-5 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent grid place-items-center shrink-0">
                    <Users className="h-[18px] w-[18px] text-primary" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[15px]">¿Usas Aviando en el trabajo?</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">Invita a tu equipo y colaborad compartiendo proyectos.</p>
                    <button type="button" onClick={() => toast("Los planes de equipo llegarán pronto.")} data-testid="invite-team-button" className="mt-2 flex items-center gap-1 text-[13px] text-primary font-medium hover:underline">
                      Invitar a mi equipo <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Privacidad y datos (ancho completo) */}
            <Card className="lg:col-span-3" data-testid="settings-data-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Privacidad y datos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="rounded-lg border p-4">
                    <Download className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                    <p className="mt-2 font-medium text-sm">Exportar mis datos</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Descarga una copia de todos tus contenidos y datos en formato JSON.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => toast("Prepararé la exportación de tus datos.", { description: "Función simulada en este prototipo." })} data-testid="export-data-button">
                      Exportar datos
                    </Button>
                  </div>
                  <div className="rounded-lg border p-4">
                    <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.75} />
                    <p className="mt-2 font-medium text-sm">Eliminar mi cuenta</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Elimina tu cuenta y todos tus datos de forma permanente.</p>
                    <Button variant="outline" size="sm" className="mt-3 text-destructive hover:text-destructive" onClick={() => toast("Esta acción estará disponible en la versión final.")} data-testid="delete-account-button">
                      Eliminar cuenta
                    </Button>
                  </div>
                  <div className="rounded-lg border p-4">
                    <Eye className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                    <p className="mt-2 font-medium text-sm">Ver y gestionar datos</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Revisa qué datos almacenamos y cómo los utilizamos.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => toast("Gestión de datos en la versión final.")}>
                      Gestionar datos
                    </Button>
                  </div>
                </div>
                <p className="mt-3 text-[13px] text-muted-foreground">
                  Cumplimos con el RGPD. Tus datos son tuyos.{" "}
                  <button type="button" onClick={() => toast("Política de privacidad en la versión final.")} className="text-primary hover:underline">
                    Ver política de privacidad →
                  </button>
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
